import { storage } from '../../storage';
import { DomainHandler, HandlerContext, IMPORT_TERMINAL_STATUSES } from '../../types';
import {
  ImportStorageState,
  DEFAULT_IMPORT_STATE,
  IMPORT_PROGRESS_TYPES,
  ImportInfo,
} from './import.types';
import { ImportStatus } from '../../../types/import.types';

const awaitingInitialProgress = new Map<string, boolean>();
const hydrationTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const completionFallbackTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const cleanupTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const HYDRATION_DELAY_MS = 1500;
const COMPLETION_FALLBACK_MS = 1500;
const CLEANUP_DELAY_DONE_MS = 2000;
const CLEANUP_DELAY_ERROR_MS = 4000;

function clearTimer(map: Map<string, ReturnType<typeof setTimeout>>, key: string): void {
  const t = map.get(key);
  if (t !== undefined) {
    clearTimeout(t);
    map.delete(key);
  }
}

function readState(key: string): ImportStorageState {
  return storage.get<ImportStorageState>(key) ?? { ...DEFAULT_IMPORT_STATE };
}

function writeState(key: string, partial: Partial<ImportStorageState>): void {
  storage.set(key, { ...readState(key), ...partial });
}

function normalizeProgress(progress?: number): number {
  if (typeof progress !== 'number' || Number.isNaN(progress)) return 0;
  const normalized = progress > 1 ? progress / 100 : progress;
  return Math.min(Math.max(normalized, 0), 1);
}

function normalizeImportInfo(info: ImportInfo): ImportInfo {
  const normalizedProgress = normalizeProgress(info.progress);
  if (info.status !== ImportStatus.DONE) {
    return { ...info, progress: normalizedProgress };
  }
  return {
    ...info,
    progress: 1,
    processed_rows: info.total_rows ?? info.processed_rows,
    remaining_seconds: 0,
  };
}

function finalize(key: string, info: ImportInfo): void {
  clearTimer(completionFallbackTimeouts, key);
  clearTimer(hydrationTimeouts, key);
  clearTimer(cleanupTimeouts, key);
  const prev = readState(key);
  writeState(key, {
    isImporting: false,
    currentImport: info,
    historyVersion: prev.historyVersion + 1,
  });
  const delay = info.status === ImportStatus.DONE ? CLEANUP_DELAY_DONE_MS : CLEANUP_DELAY_ERROR_MS;
  const t = setTimeout(() => {
    writeState(key, { currentImport: null });
  }, delay);
  cleanupTimeouts.set(key, t);
}

export const importHandler: DomainHandler = {
  onOpen(ctx: HandlerContext): void {
    const key = ctx.descriptor.storageKey;
    awaitingInitialProgress.set(key, true);
    clearTimer(hydrationTimeouts, key);
    ctx.send({ action: 'is_importing' });
    ctx.send({ action: 'get_progress' });
  },

  onMessage(msg: unknown, ctx: HandlerContext): boolean {
    const key = ctx.descriptor.storageKey;

    if (typeof msg !== 'object' || msg === null) return false;
    const m = msg as Record<string, unknown>;

    // Branch a: is_importing response
    if (m.action === 'is_importing') {
      if (m.is_importing === true) {
        clearTimer(hydrationTimeouts, key);
        awaitingInitialProgress.set(key, false);
        writeState(key, { isImporting: true });
      } else {
        const finalizeNoImportState = () => {
          awaitingInitialProgress.set(key, false);
          const prev = readState(key);
          if (prev.currentImport) {
            writeState(key, {
              isImporting: false,
              currentImport: { ...prev.currentImport, status: ImportStatus.DONE, progress: 1 },
              historyVersion: prev.historyVersion + 1,
            });
          } else {
            writeState(key, { isImporting: false });
          }
        };

        if (awaitingInitialProgress.get(key)) {
          clearTimer(hydrationTimeouts, key);
          const t = setTimeout(finalizeNoImportState, HYDRATION_DELAY_MS);
          hydrationTimeouts.set(key, t);
        } else {
          finalizeNoImportState();
        }
      }
      return false;
    }

    // Branch b: progress messages
    const hasProgressType =
      typeof m.type === 'string' &&
      (IMPORT_PROGRESS_TYPES as readonly string[]).includes(m.type);
    const isGetProgressResponse = m.action === 'get_progress' && m.data != null;

    if (hasProgressType || isGetProgressResponse) {
      clearTimer(hydrationTimeouts, key);
      awaitingInitialProgress.set(key, false);

      const data = m.data as Record<string, unknown>;
      if (!data) return false;

      const prev = readState(key);
      const merged = {
        ...(prev.currentImport ?? {}),
        ...(data as Partial<ImportInfo>),
      } as ImportInfo;
      const next = normalizeImportInfo(merged);

      if ((IMPORT_TERMINAL_STATUSES as readonly string[]).includes(next.status)) {
        clearTimer(cleanupTimeouts, key);
        finalize(key, next);
        return false;
      }

      clearTimer(cleanupTimeouts, key);
      writeState(key, { isImporting: true, currentImport: next });

      const isActive =
        next.status === ImportStatus.PENDING || next.status === ImportStatus.RUNNING;
      if (isActive && (next.progress ?? 0) >= 1) {
        clearTimer(completionFallbackTimeouts, key);
        const t = setTimeout(() => {
          finalize(key, { ...next, status: ImportStatus.DONE });
        }, COMPLETION_FALLBACK_MS);
        completionFallbackTimeouts.set(key, t);
      } else {
        clearTimer(completionFallbackTimeouts, key);
      }
    }

    return false;
  },

  onClose(_event: CloseEvent, ctx: HandlerContext): void {
    const key = ctx.descriptor.storageKey;
    clearTimer(hydrationTimeouts, key);
    clearTimer(completionFallbackTimeouts, key);
    clearTimer(cleanupTimeouts, key);
    awaitingInitialProgress.delete(key);
  },
};
